import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  Pagination,
  Stack,
} from "@mui/material";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import "../../styles/table.scss";

const GenericTable = ({
  columns = [],
  data = [],
  isLoading = false,
  paginationModel = { page: 0, pageSize: 10 },
  setPaginationModel,
  maxHeight = "calc(100vh - 240px)",
  totalRow = 0,
}) => {
  const renderSkeletonRow = () => {
    return (
      <TableRow key={`skeleton`} className="skeleton-row">
        {columns.map((column, colIndex) => (
          <TableCell
            key={`skeleton-cell-${colIndex}`}
            className="skeleton-cell"
            style={{ flex: column?.flex || 1 }}
          >
            <Skeleton variant="text" animation="wave" width="80%" height={20} />
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const onPageChange = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const onRowsPerPageChange = (event) => {
    setPaginationModel({
      ...paginationModel,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  return (
    <>
      <Box component={Paper} className="table-wrapper">
        <TableContainer className="table-container" style={{ maxHeight }}>
          {isLoading && (
            <Box className="loader-overlay">
              <CircularProgress size={32} className="loader-spinner" />
            </Box>
          )}
          <Table stickyHeader>
            <TableHead className="table-head">
              <TableRow className="header-row ">
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    className={`header-cell ${
                      column.isSticky ? "sticky-cell" : ""
                    }`}
                    style={{
                      flex: column?.flex || 1,
                      minWidth: column?.minWidth,
                    }}
                  >
                    {column?.renderHeader
                      ? column?.renderHeader()
                      : column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: paginationModel.pageSize }).map(
                    (_, index) => (
                      <React.Fragment key={index}>
                        {renderSkeletonRow()}
                      </React.Fragment>
                    )
                  )
                : data.length
                ? data.map((row, rowIndex) => (
                    <TableRow key={row.id || rowIndex} className="data-row">
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={colIndex}
                          className={`data-cell ${
                            column.isSticky ? "sticky-cell" : ""
                          }`}
                          style={{
                            flex: column?.flex || 1,
                            minWidth: column?.minWidth,
                          }}
                        >
                          {column?.renderCell
                            ? column?.renderCell({
                                row,
                                value: row[column.field],
                              })
                            : row[column.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : !isLoading && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Box className="no-data-box">
                          <ErrorOutlineOutlined className="no-data-icon" />
                          <Typography className="no-data-text">
                            No Data Found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length ? (
          <Box className="pagination-wrapper">
            <Box className="rows-per-page">
              <Typography className="rows-per-page-text">
                Rows Per Page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(event) => onRowsPerPageChange(event)}
                size="small"
                className="rows-per-page-select"
              >
                {[5, 10, 20, 50].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Stack spacing={2}>
              <Pagination
                count={Math.ceil(totalRow / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                variant="outlined"
                shape="rounded"
                onChange={(event, value) => {
                  onPageChange(event, value - 1);
                }}
              />
            </Stack>
          </Box>
        ) : (
          ""
        )}
      </Box>
    </>
  );
};

export default GenericTable;
